import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCustomers } from "../../../../../api/customers";
import { getDisplayAccountType } from "../../../../../utils/accountTypeUtils";
import { buildPlatformCustomerScopeParams } from "../../utils/platformScope";

export default function useReportsTabData({ profile, rowsPerPage, sortConfig }) {
  const [realData, setRealData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [activeManagerFilter, setActiveManagerFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);

  const fetchRealDataRef = useRef();
  const latestRequestIdRef = useRef(0);

  const transformCustomerToReport = useCallback((customer) => {
    const getStatus = (entry) => {
      return entry.onboarding_stage === "completed" ? "Ready" : "Onboarding";
    };

    const getBookkeeper = (entry) => {
      return entry.bookkeeper || "";
    };

    return {
      id: customer.id,
      accountName: customer.name || "",
      primaryContact: customer.primary_contact || "",
      primaryEmail: customer.email || "",
      accountType: getDisplayAccountType(customer),
      status: getStatus(customer),
      manager: customer.manager || "",
      bookkeeper: getBookkeeper(customer),
      dashboardDate: customer.dashboard_date || null,
      originalCustomer: customer,
      customerId: customer.id,
    };
  }, []);

  const fetchRealData = useCallback(
    async (cursor = null, search = "", isNewSearch = false, filters = {}) => {
      if (!profile) {
        setRealData([]);
        setNextCursor(null);
        setPrevCursor(null);
        setLoading(true);
        return;
      }

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      setLoading(true);
      setError(null);

      try {
        const params = {
          page_size: rowsPerPage,
          include_profiles: false,
          include_quickbooks_connection: true,
        };

        if (cursor) {
          params.cursor = cursor;
        }

        if (search && search.trim()) {
          params.search = search.trim();
        }

        if (filters.accountType && filters.accountType.trim()) {
          params.account_types = filters.accountType.trim();
        }

        if (filters.accountName && filters.accountName.trim()) {
          params.name = filters.accountName.trim();
        }

        if (filters.primaryContact && filters.primaryContact.trim()) {
          params.primary_contact = filters.primaryContact.trim();
        }

        if (filters.primaryEmail && filters.primaryEmail.trim()) {
          params.email = filters.primaryEmail.trim();
        }

        if (filters.manager && filters.manager.trim()) {
          params.manager = filters.manager.trim();
        }

        if (filters.bookkeeper && filters.bookkeeper.trim()) {
          params.bookkeeper = filters.bookkeeper.trim();
        }

        if (sortConfig && sortConfig.field && sortConfig.direction) {
          const fieldMap = {
            accountName: "name",
            primaryContact: "primary_contact",
            primaryEmail: "email",
            manager: "manager",
            bookkeeper: "bookkeeper",
          };
          const backendField = fieldMap[sortConfig.field] || sortConfig.field;
          params.ordering = sortConfig.direction === "asc" ? backendField : `-${backendField}`;
        }

        const response = await fetchCustomers(
          profile,
          buildPlatformCustomerScopeParams(profile, params)
        );

        if (requestId !== latestRequestIdRef.current) return;

        const customers = response.results || [];
        const reports = customers.map((customer) => transformCustomerToReport(customer));

        setRealData(reports);
        setNextCursor(response.next ? new URL(response.next).searchParams.get("cursor") : null);
        setPrevCursor(response.previous ? new URL(response.previous).searchParams.get("cursor") : null);
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return;
        setError("Failed to load reports data");
        setRealData([]);
        setNextCursor(null);
        setPrevCursor(null);
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [profile, rowsPerPage, sortConfig, transformCustomerToReport]
  );

  const handleNextPage = useCallback(() => {
    if (nextCursor) {
      setPage((prev) => prev + 1);
      fetchRealDataRef.current(nextCursor, searchTerm, false, activeFilters);
    }
  }, [nextCursor, searchTerm, activeFilters]);

  const handlePreviousPage = useCallback(() => {
    if (prevCursor) {
      setPage((prev) => prev - 1);
      fetchRealDataRef.current(prevCursor, searchTerm, false, activeFilters);
    } else {
      setPage(0);
      fetchRealDataRef.current(null, searchTerm, false, activeFilters);
    }
  }, [prevCursor, searchTerm, activeFilters]);

  const handleSearchChange = useCallback(
    (searchValue) => {
      if (searchValue === searchTerm) {
        return;
      }

      setSearchTerm(searchValue);
      setPage(0);
      fetchRealDataRef.current(null, searchValue, true, activeFilters);
    },
    [searchTerm, activeFilters]
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      setActiveFilters(newFilters);
      setPage(0);
      fetchRealDataRef.current(null, searchTerm, true, newFilters);
    },
    [searchTerm]
  );

  const handleManagerFilterChange = useCallback(
    (managers) => {
      if (!managers || managers.length === 0) {
        const newFilters = { ...activeFilters };
        delete newFilters.manager;
        setActiveFilters(newFilters);
        setActiveManagerFilter(null);
        setPage(0);
        fetchRealDataRef.current(null, searchTerm, true, newFilters);
      } else {
        const newFilters = { ...activeFilters, manager: managers.join(",") };
        setActiveFilters(newFilters);
        setActiveManagerFilter(managers);
        setPage(0);
        fetchRealDataRef.current(null, searchTerm, true, newFilters);
      }
    },
    [searchTerm, activeFilters]
  );

  const refresh = useCallback(() => {
    fetchRealDataRef.current(null, searchTerm, true, activeFilters);
  }, [searchTerm, activeFilters]);

  const updateDashboardDate = useCallback((customerId, dashboardDate) => {
    setRealData((currentRows) =>
      currentRows.map((row) =>
        row.id === customerId
          ? {
              ...row,
              dashboardDate,
              originalCustomer: {
                ...row.originalCustomer,
                dashboard_date: dashboardDate,
              },
            }
          : row
      )
    );
  }, []);

  fetchRealDataRef.current = fetchRealData;

  useEffect(() => {
    if (!profile) {
      latestRequestIdRef.current += 1;
      setRealData([]);
      setNextCursor(null);
      setPrevCursor(null);
      setLoading(true);
      return;
    }

    fetchRealDataRef.current(null, "", true, {});
  }, [profile]);

  useEffect(() => {
    if (!profile) {
      return;
    }

    setPage(0);
    fetchRealDataRef.current(null, searchTerm, true, activeFilters);
  }, [profile, rowsPerPage, sortConfig, searchTerm, activeFilters]);

  return {
    data: realData,
    loading,
    error,
    searchTerm,
    page,
    nextCursor,
    prevCursor,
    activeManagerFilter,
    handleNextPage,
    handlePreviousPage,
    handleSearchChange,
    handleFiltersChange,
    handleManagerFilterChange,
    refresh,
    updateDashboardDate,
  };
}
